import React, { useState } from 'react';
import { Box, Tabs, Tab, IconButton, Tooltip, Grid } from '@mui/material';
import api from 'lib/trpcClient';
import Table from 'components/Table';
import {
  numberToCurrencyString,
  dateToLabel,
  numberToDisplayString,
} from 'further-ui/utils';
import { CompareArrows, Close } from '@mui/icons-material';
import {
  createInvestmentEditRoute,
  createInvestmentViewLink,
} from 'adminConstants/routes';
import Spacing from 'components/Spacing';
import GridContainer from 'components/GridContainer';
import { styled } from '@mui/material/styles';
import { ConsiderationHistoryItemType } from 'further-types/exit';
import { round } from 'lodash';

const TrancheCell = styled('span')(() => ({
  display: 'inline-block',
  minWidth: '7rem',
}));

const TooltipLink = styled('a')(({ theme }) => ({
  color: theme.palette.text.rootColor,
}));

const StyledTabsWrapper = styled(Box)(() => ({
  width: '100%',
  borderBottom: '1px solid #e0e0e0',
}));

const StyledTableWrapper = styled(Box)(() => ({
  width: '100%',
  marginTop: '1rem',
  overflowX: 'auto',
  maxWidth: '100%',
}));

const StyledTab = styled(Tab)(() => ({
  '& .MuiTab-wrapper': {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
}));

const StyledTabLabel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
}));

const StyledIconButton = styled(IconButton)(() => ({
  padding: '2px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

type ConsiderationHistoryProps = {
  exitId: string;
  firmId: string;
  isBeingCorrected?: boolean;
};

type ShareConsiderationItem = {
  shareConsiderationValuePerShare?: number;
  shareConsiderationValueTotal?: number;
  tranche?: { fundName?: string };
  sourceInvestmentId?: string;
  investor?: { fullName?: string };
  transferSourceInvestor?: { fullName?: string };
  transferDate?: string;
  investmentDate?: string;
  allocationDate?: string;
  noOfShares?: number;
  sharesAllocatedForSale?: number;
  newSharesAllocated?: number;
  company?: { tradingName?: string };
  externalInvestmentId?: string | null;
};

type CashConsiderationItem = {
  tranche?: { fundName?: string };
  sourceInvestmentId?: string;
  investor?: { fullName?: string };
  transferSourceInvestor?: { fullName?: string };
  transferDate?: string;
  investmentDate?: string;
  allocationDate?: string;
  sharesAllocatedForSale?: number;
  noOfShares?: number;
  sharePrice?: number;
  totalSaleValue?: number;
  additionalGrossReceivedValue?: number;
  performanceFee?: number;
  additionalPerformanceFee?: number;
  accruedFeesCharged?: number;
  taxableValue?: number;
  netReturn?: number;
  company?: { tradingName?: string };
  externalInvestmentId?: string | null;
  shareClass?: string;
};

const ConsiderationHistory: React.FC<ConsiderationHistoryProps> = ({
  exitId,
  firmId,
  isBeingCorrected,
}) => {
  const [selectedTab, setSelectedTab] = useState<number | null>(null);

  const considerationHistory = api.exit.getConsiderationHistory.useQuery(
    {
      exitId,
      firmId,
    },
    {
      enabled: !!exitId && !!firmId,
    },
  );

  // Filter out the latest amendment if we're correcting (it's shown in the main table)
  const filteredHistory = isBeingCorrected
    ? considerationHistory.data?.slice(0, -1) || []
    : considerationHistory.data || [];

  // Don't show the section if no history after filtering
  if (!filteredHistory.length) {
    return null;
  }

  const shareConsiderationColumns = [
    {
      label: 'Tranche',
      key: 'tranche',
      sort: false,
      render: (elm: ShareConsiderationItem) => (
        <TrancheCell>{elm.tranche?.fundName ?? ''}</TrancheCell>
      ),
    },
    {
      label: 'Name',
      key: 'name',
      sort: false,
      render: (elm: ShareConsiderationItem) => {
        if (!elm.sourceInvestmentId) {
          return elm.investor?.fullName ?? '';
        }

        const sourceInvestorName = elm.transferSourceInvestor?.fullName;
        return (
          <Tooltip
            title={
              <div>
                This investor is the recipient of a transfer from{' '}
                <TooltipLink
                  href={createInvestmentEditRoute(elm.sourceInvestmentId)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {sourceInvestorName}
                </TooltipLink>{' '}
                on {dateToLabel(elm.transferDate)}.
              </div>
            }
          >
            <Box alignItems="center" display="flex" gap={4}>
              {elm.investor?.fullName ?? ''}
              <CompareArrows />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      label: 'Subscription date',
      key: 'subscriptionDate',
      sort: false,
      render: (elm: ShareConsiderationItem) =>
        elm.investmentDate ? dateToLabel(elm.investmentDate) : '-',
    },
    {
      label: 'Allocation date',
      key: 'allocationDate',
      sort: false,
      render: (elm: ShareConsiderationItem) =>
        elm.allocationDate ? dateToLabel(elm.allocationDate) : '-',
    },
    {
      label: 'Shares held',
      key: 'noOfShares',
      sort: false,
      render: (elm: ShareConsiderationItem) =>
        numberToDisplayString(elm.noOfShares ?? 0, {
          maximumFractionDigits: 10,
        }),
    },
    {
      label: 'Exit shares sold',
      key: 'sharesAllocatedForSale',
      sort: false,
      render: (elm: ShareConsiderationItem) =>
        numberToDisplayString(elm.sharesAllocatedForSale ?? 0, {
          maximumFractionDigits: 10,
        }),
    },
    {
      label: 'New shares allocated',
      key: 'newSharesAllocated',
      sort: false,
      render: (elm: ShareConsiderationItem) =>
        numberToDisplayString(elm?.newSharesAllocated ?? 0, {
          maximumFractionDigits: 10,
        }),
    },
    {
      label: 'Company',
      key: 'company',
      sort: false,
      render: (elm: ShareConsiderationItem) => elm.company?.tradingName ?? '-',
    },
    {
      label: 'Share value',
      key: 'shareValue',
      sort: false,
      render: (elm: ShareConsiderationItem) => {
        if (!elm.newSharesAllocated || elm.newSharesAllocated === 0) {
          return '-';
        }
        const totalValue = round(
          (elm.shareConsiderationValuePerShare ?? 0) * elm.newSharesAllocated,
          2,
        );
        return numberToCurrencyString(totalValue, {
          fallback: '-',
        });
      },
    },
    {
      label: 'External subscription ID',
      key: 'externalInvestmentId',
      sort: false,
      render: (elm: ShareConsiderationItem) => elm.externalInvestmentId ?? '-',
    },
  ];

  const getCashConsiderationColumns = (isFirstCashConsideration: boolean) => [
    {
      label: 'Investor name',
      key: 'investorName',
      sort: false,
      render: (elm: CashConsiderationItem) => {
        if (!elm.sourceInvestmentId) {
          return elm.investor?.fullName ?? '';
        }

        const sourceInvestorName = elm.transferSourceInvestor?.fullName;
        return (
          <Tooltip
            title={
              <div>
                This investor is the recipient of a transfer from{' '}
                <TooltipLink
                  href={createInvestmentViewLink(elm.sourceInvestmentId)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {sourceInvestorName}
                </TooltipLink>{' '}
                on {dateToLabel(elm.transferDate)}.
              </div>
            }
          >
            <Box alignItems="center" display="flex" gap={4}>
              {elm.investor?.fullName ?? ''}
              <CompareArrows />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      label: 'Tranche',
      key: 'tranche',
      sort: false,
      render: (elm: CashConsiderationItem) => (
        <TrancheCell>{elm.tranche?.fundName ?? ''}</TrancheCell>
      ),
    },
    {
      label: 'Allocation',
      key: 'allocationDateWithShareClass',
      sort: false,
      render: (elm: CashConsiderationItem) => (
        <>
          {elm.allocationDate ? dateToLabel(elm.allocationDate) : '-'}
          <br />({elm.shareClass ?? '-'})
        </>
      ),
    },
    {
      label: 'Shares held',
      key: 'noOfShares',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToDisplayString(elm.noOfShares ?? 0, {
          maximumFractionDigits: 10,
        }),
    },
    {
      label: 'Shares sold',
      key: 'sharesAllocatedForSale',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToDisplayString(elm.sharesAllocatedForSale ?? 0, {
          maximumFractionDigits: 10,
        }),
    },
    {
      label: 'Received value',
      key: 'totalSaleValue',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.totalSaleValue, {
          fallback: '-',
        }),
    },
    ...(isFirstCashConsideration
      ? []
      : [
          {
            label: 'Additional received value',
            key: 'additionalGrossReceivedValue',
            sort: false,
            render: (elm: CashConsiderationItem) =>
              numberToCurrencyString(elm.additionalGrossReceivedValue, {
                fallback: '-',
              }),
          },
        ]),
    {
      label: 'Performance fee',
      key: 'performanceFee',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.performanceFee, {
          fallback: '-',
        }),
    },
    ...(isFirstCashConsideration
      ? []
      : [
          {
            label: 'Additional performance fee',
            key: 'additionalPerformanceFee',
            sort: false,
            render: (elm: CashConsiderationItem) =>
              numberToCurrencyString(elm.additionalPerformanceFee, {
                fallback: '-',
              }),
          },
        ]),
    {
      label: 'Accrued fees',
      key: 'accruedFeesCharged',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.accruedFeesCharged, {
          fallback: '-',
        }),
    },
    {
      label: 'Net return',
      key: 'netReturn',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.netReturn, {
          fallback: '-',
        }),
    },
    {
      label: 'Taxable value share price',
      key: 'sharePrice',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.sharePrice, {
          fallback: '-',
        }),
    },
    {
      label: 'Net taxable value',
      key: 'taxableValue',
      sort: false,
      render: (elm: CashConsiderationItem) =>
        numberToCurrencyString(elm.taxableValue, {
          fallback: '-',
        }),
    },
  ];

  if (considerationHistory.isLoading) {
    return null;
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleRemoveTab = (event: React.MouseEvent, tabIndex: number) => {
    event.stopPropagation();

    if (selectedTab === tabIndex) {
      setSelectedTab(null);
    }
  };

  const getTabLabel = (
    type: ConsiderationHistoryItemType,
    currentIndex: number,
    allItems: Array<{ type: ConsiderationHistoryItemType }>,
  ): string => {
    const typeLabel =
      type === ConsiderationHistoryItemType.ShareConsiderationExit
        ? 'Share consideration'
        : 'Cash consideration';

    // Count how many items of the same type appear before the current index
    const sameTypeCount = allItems
      .slice(0, currentIndex + 1)
      .filter((item) => item.type === type).length;

    return `${typeLabel} ${sameTypeCount}`;
  };

  const selectedItem =
    selectedTab !== null ? filteredHistory[selectedTab] : null;

  // Check if this is the first cash consideration (all items have undefined additionalGrossReceivedValue)
  const isFirstCashConsideration =
    selectedItem?.type === ConsiderationHistoryItemType.CashExit &&
    selectedItem.data.every(
      (item: CashConsiderationItem) =>
        item.additionalGrossReceivedValue === undefined &&
        item.additionalPerformanceFee === undefined,
    );

  return (
    <>
      <GridContainer>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Spacing>
            <StyledTabsWrapper>
              <Tabs
                value={selectedTab ?? false}
                onChange={handleTabChange}
                variant="scrollable"
              >
                {filteredHistory.map((item, index) => (
                  <StyledTab
                    key={`${item.exitId}-${index}`}
                    value={index}
                    label={
                      <StyledTabLabel>
                        {selectedTab === index && (
                          <StyledIconButton
                            size="small"
                            onClick={(e) => handleRemoveTab(e, index)}
                          >
                            <Close fontSize="small" />
                          </StyledIconButton>
                        )}
                        <span>
                          {getTabLabel(item.type, index, filteredHistory)}
                        </span>
                      </StyledTabLabel>
                    }
                  />
                ))}
              </Tabs>
            </StyledTabsWrapper>
          </Spacing>
        </Grid>
      </GridContainer>
      {selectedTab !== null && selectedItem && (
        <StyledTableWrapper>
          <Table
            columns={
              selectedItem.type ===
              ConsiderationHistoryItemType.ShareConsiderationExit
                ? shareConsiderationColumns
                : getCashConsiderationColumns(isFirstCashConsideration)
            }
            tablebody={selectedItem.data}
            loading={false}
            emptyMessage={
              selectedItem.type ===
              ConsiderationHistoryItemType.ShareConsiderationExit
                ? 'No share considerations found.'
                : 'No cash exit data found.'
            }
            variant="nohover"
          />
        </StyledTableWrapper>
      )}
    </>
  );
};

export default ConsiderationHistory;
